import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/articles';
import type { ArticleSection } from '@/mocks/articles';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://superbuyspread.study';

function buildArticleJsonLd(article: typeof articles[0], articleUrl: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    image: article.heroImage,
    author: {
      '@type': 'Organization',
      name: 'Superbuy Research Team',
      url: siteUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Superbuy Spreadsheet',
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/67defc9f4dfae2c04004e8ea78b88107.png',
      },
    },
    datePublished: '2026-07-01',
    dateModified: '2026-07-12',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleUrl,
    },
  };
}

function buildBreadcrumbJsonLd(article: typeof articles[0], articleUrl: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: siteUrl,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: article.title,
        item: articleUrl,
      },
    ],
  };
}

function buildFaqJsonLd(sections: ArticleSection[]) {
  const faqSection = sections.find((s) => s.type === 'faq');
  if (!faqSection || !faqSection.items) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqSection.items.map((item) => ({
      '@type': 'Question',
      name: item.title || '',
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.desc,
      },
    })),
  };
}

function renderSection(section: ArticleSection, index: number) {
  switch (section.type) {
    case 'heading':
      return (
        <h2 key={index} className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mt-10 md:mt-12 mb-4 first:mt-0">
          {section.content}
        </h2>
      );

    case 'paragraph':
      return (
        <p key={index} className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
          {section.content}
        </p>
      );

    case 'image':
      return (
        <div key={index} className="my-8 rounded-2xl overflow-hidden bg-background-100">
          <img
            src={section.imageUrl}
            alt={section.imageAlt || ''}
            className="w-full h-auto object-cover"
            loading="lazy"
          />
        </div>
      );

    case 'list':
      return (
        <div key={index} className="bg-background-100 rounded-2xl p-5 md:p-6 mb-6">
          <ul className="space-y-4">
            {section.items?.map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mt-0.5">
                  <i className="ri-check-line text-xs"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-800">{item.title}</p>
                  <p className="text-xs md:text-sm text-foreground-500 mt-0.5">{item.desc}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      );

    case 'steps':
      return (
        <div key={index} className="space-y-5 mb-6">
          {section.steps?.map((step, i) => (
            <div key={i} className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap">
                  {step.step}
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-sm md:text-base text-foreground-950 mb-1">{step.title}</h3>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>
      );

    case 'tips':
      return (
        <div key={index} className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          {section.items?.map((tip, i) => (
            <div key={i} className="bg-background-100 rounded-xl p-4 md:p-5">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 flex items-center justify-center rounded-md bg-accent-100 text-accent-600">
                  <i className="ri-lightbulb-line text-sm"></i>
                </div>
                <h4 className="font-semibold text-sm text-foreground-950">{tip.title}</h4>
              </div>
              <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">{tip.desc}</p>
            </div>
          ))}
        </div>
      );

    case 'comparison':
      return (
        <div key={index} className="space-y-4 mb-6">
          {section.items?.map((item, i) => (
            <div key={i} className="border border-background-200/70 rounded-xl p-4 md:p-5">
              <h4 className="font-semibold text-sm text-foreground-950 mb-1">{item.title}</h4>
              <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      );

    case 'faq':
      return (
        <div key={index} className="space-y-4 mb-6">
          {section.items?.map((faq, i) => (
            <div key={i} className="border border-background-200/70 rounded-xl p-4 md:p-5">
              <h3 className="font-semibold text-sm md:text-base text-foreground-950 mb-1">{faq.title}</h3>
              <p className="text-sm md:text-base text-foreground-500 leading-relaxed">{faq.desc}</p>
            </div>
          ))}
        </div>
      );

    case 'cta':
      return (
        <div key={index} className="bg-primary-50 rounded-2xl p-5 md:p-6 border border-primary-100 mt-10">
          <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-950 mb-2">
            Ready to put this knowledge into action?
          </h3>
          <p className="text-sm md:text-base text-foreground-600 mb-4">
            {section.content}
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Visit Kaspreadsheet
              <i className="ri-arrow-right-line text-xs"></i>
            </a>
            <Link
              to="/"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-primary-300 text-primary-700 text-sm font-medium hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              Back to Superbuy Spreadsheet Hub
              <i className="ri-home-line text-xs"></i>
            </Link>
          </div>
        </div>
      );

    default:
      return null;
  }
}

export default function ArticleDetail() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-24 md:pt-32 pb-14 min-h-[60vh] flex items-center justify-center bg-background-50">
          <div className="text-center px-4">
            <h1 className="font-heading text-2xl font-semibold text-foreground-950 mb-3">Article Not Found</h1>
            <p className="text-foreground-500 mb-6">The article you are looking for does not exist.</p>
            <Link to="/" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-home-line text-xs"></i>
              Back to Home
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleUrl = `${siteUrl}/article/${article.slug}`;

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(buildArticleJsonLd(article, articleUrl)) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(buildBreadcrumbJsonLd(article, articleUrl)) }}
      />
      {buildFaqJsonLd(article.sections) && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(buildFaqJsonLd(article.sections)) }}
        />
      )}
      <Navbar />
      <main>
        {/* Article Header */}
        <section className="pt-24 md:pt-32 pb-8 md:pb-10 bg-background-50">
          <div className="w-full max-w-[1440px] mx-auto px-4 md:px-8">
            <div className="max-w-3xl mx-auto">
              {/* Breadcrumb */}
              <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6" aria-label="Breadcrumb">
                <Link to="/" className="hover:text-primary-600 transition-colors">Home</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-800 font-medium truncate max-w-[200px] md:max-w-xs">{article.title}</span>
              </nav>

              <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-3">
                {article.category}
              </span>
              <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-semibold text-foreground-950 leading-tight mb-4">
                {article.title}
              </h1>
              <p className="text-sm md:text-base text-foreground-500 leading-relaxed mb-6">
                {article.description}
              </p>
              <div className="flex flex-wrap items-center gap-4 text-xs md:text-sm text-foreground-400 mb-8">
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i> Updated July 2026
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i> {article.readTime}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i> By Superbuy Research Team
                </span>
              </div>

              {/* Hero Image */}
              <div className="rounded-2xl overflow-hidden bg-background-100 mb-10">
                <img
                  src={article.heroImage}
                  alt={article.title}
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <article className="pb-14 md:pb-20 bg-background-50">
          <div className="w-full max-w-[1440px] mx-auto px-4 md:px-8">
            <div className="max-w-3xl mx-auto">
              {article.sections.map((section, index) => renderSection(section, index))}

              {/* Bottom navigation */}
              <div className="mt-12 pt-8 border-t border-background-200/70">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 text-sm text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-arrow-left-line text-xs"></i>
                    Back to Superbuy Spreadsheet Hub
                  </Link>
                  <div className="flex flex-wrap gap-2">
                    {articles.filter(a => a.slug !== article.slug).slice(0, 3).map((a) => (
                      <Link
                        key={a.slug}
                        to={`/article/${a.slug}`}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-background-100 text-xs text-foreground-600 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-article-line"></i>
                        {a.category}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}